import { useEffect, useRef } from 'react';

const VERT = `
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const FRAG = `
precision highp float;
uniform float uTime;
uniform vec2 uResolution;
uniform vec3 uColorA;
uniform vec3 uColorB;
uniform vec3 uBase;
varying vec2 vUv;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p *= 2.0;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = vUv;
  float t = uTime * 0.15;

  float angle = atan(uv.y - 0.15, uv.x - 0.05);
  float dist = length(uv - vec2(0.05, 0.15));

  float rays = 0.0;
  rays += smoothstep(0.4, 0.0, abs(sin(angle * 3.0 + t * 0.5 + fbm(uv * 2.0 + t)) * 0.5)) * 0.3;
  rays += smoothstep(0.3, 0.0, abs(sin(angle * 5.0 - t * 0.3 + fbm(uv * 3.0 - t * 0.5)) * 0.5)) * 0.2;
  rays += smoothstep(0.35, 0.0, abs(sin(angle * 7.0 + t * 0.7 + fbm(uv * 1.5 + t * 0.3)) * 0.5)) * 0.15;

  rays *= smoothstep(2.0, 0.0, dist) * smoothstep(0.0, 0.3, dist);

  float fog = fbm(uv * 1.5 + t * 0.2) * 0.15;
  fog += fbm(uv * 3.0 - t * 0.1) * 0.08;

  float intensity = rays + fog;

  vec3 color = mix(uBase, mix(uColorA, uColorB, uv.y + sin(t) * 0.1), intensity);

  // Vignette fades the corners toward uBase (instead of toward 0), so the
  // shader works on light backgrounds without producing dark halos.
  float vig = 1.0 - smoothstep(0.4, 1.4, length(uv - 0.5) * 1.2);
  color = mix(uBase, color, vig);

  gl_FragColor = vec4(color, 1.0);
}`;

function initGL(canvas, colorA, colorB, base) {
  const gl = canvas.getContext('webgl', { alpha: false, antialias: false, preserveDrawingBuffer: false });
  if (!gl) return null;

  const compile = (type, src) => {
    const s = gl.createShader(type);
    if (!s) return null;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(s));
      gl.deleteShader(s);
      return null;
    }
    return s;
  };

  const vs = compile(gl.VERTEX_SHADER, VERT);
  const fs = compile(gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) return null;

  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(prog));
    return null;
  }

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 3,-1, -1,3]), gl.STATIC_DRAW);

  const posLoc = gl.getAttribLocation(prog, 'position');
  gl.useProgram(prog);
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

  const hex = (h) => {
    const c = (h || '#45526d').replace('#', '');
    return [parseInt(c.slice(0,2),16)/255, parseInt(c.slice(2,4),16)/255, parseInt(c.slice(4,6),16)/255];
  };

  // Default base = the dark page bg `#020408` so older callers that don't
  // pass a base get the original behaviour.
  gl.uniform3fv(gl.getUniformLocation(prog, 'uColorA'), hex(colorA));
  gl.uniform3fv(gl.getUniformLocation(prog, 'uColorB'), hex(colorB));
  gl.uniform3fv(gl.getUniformLocation(prog, 'uBase'), hex(base || '#020408'));

  return {
    gl,
    uTime: gl.getUniformLocation(prog, 'uTime'),
    uRes: gl.getUniformLocation(prog, 'uResolution'),
  };
}

export default function LightRays({ style, className, colorA, colorB, base }) {
  const canvasRef = useRef(null);
  // Use a ref to track the GL state so strict-mode re-mount works on a fresh canvas
  const glRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Force a fresh canvas by resetting its dimensions (clears any prior context)
    canvas.width = 0;
    canvas.height = 0;

    const ctx = initGL(canvas, colorA, colorB, base);
    if (!ctx) return;

    glRef.current = ctx;
    const { gl, uTime, uRes } = ctx;
    const dpr = Math.min(window.devicePixelRatio, 2);

    const resize = () => {
      const w = canvas.parentElement?.offsetWidth || window.innerWidth;
      const h = canvas.parentElement?.offsetHeight || window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener('resize', resize);

    let raf;
    let stopped = false;
    const t0 = performance.now();
    const draw = () => {
      if (stopped) return;
      raf = requestAnimationFrame(draw);
      gl.uniform1f(uTime, (performance.now() - t0) * 0.001);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    draw();

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      // Don't call loseContext — let the canvas reset on next mount handle it
      glRef.current = null;
    };
  }, [colorA, colorB, base]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        display: 'block',
        ...style,
      }}
      className={className}
    />
  );
}
